import { RichEditor } from '@odo/shared-ui';
import { EventPopupSection } from './EventPopupSection';
import {
    EMPTY_TIPTAP_DOC,
    FIELD_LABEL_CLASS,
    INPUT_CLASS,
    SUBSECTION_LABEL_CLASS,
    type SectionProps
} from './eventFormShared';

const ERROR_CLASS = 'border-red-300 focus:ring-red-500 focus:border-red-500';

function inputClass(hasError: boolean): string {
    return hasError ? `${INPUT_CLASS} ${ERROR_CLASS}` : INPUT_CLASS;
}

// Paragraph text is the browser default 16px/1.5 line-height = 24px (`prose
// prose-sm` is a no-op - no typography plugin), plus `py-2.5` (10px) padding.
const BODY_EDITOR_MIN_HEIGHT_PX = 4 * 24 + 2 * 10;

// The dashboard banner strip: title, subtitle and the banner-only badge tag.
// The pop-up authors its own title/subtitle in `EventPopupSection`, prepopulated
// from these on load or as the author types (until edited directly).
function BannerFields({ draft, set, errors, touched }: SectionProps) {
    return (
        <div>
            <label className={SUBSECTION_LABEL_CLASS}>Banner</label>
            <div className="mt-2 grid grid-cols-1 gap-4">
                <div>
                    <label className={FIELD_LABEL_CLASS}>Title *</label>
                    <input
                        type="text"
                        value={draft.title}
                        onChange={(e) => set('title', e.target.value)}
                        className={inputClass(touched && Boolean(errors.title))}
                    />
                </div>
                <div>
                    <label className={FIELD_LABEL_CLASS}>Badge tag (banner only, optional)</label>
                    <input
                        type="text"
                        value={draft.badgeTag}
                        onChange={(e) => set('badgeTag', e.target.value)}
                        placeholder="Weekly · Optional"
                        className={INPUT_CLASS}
                    />
                    <p className="text-[11px] text-gray-400 mt-1">
                        Shown on the banner strip, left-aligned or centered - never in the pop-up.
                    </p>
                </div>
                <div>
                    <label className={FIELD_LABEL_CLASS}>Subtitle *</label>
                    <input
                        type="text"
                        value={draft.subtitle}
                        onChange={(e) => set('subtitle', e.target.value)}
                        className={inputClass(touched && Boolean(errors.subtitle))}
                    />
                </div>
            </div>
        </div>
    );
}

function BodyEditor({ draft, set, canManage, isAnnouncement, onImageUpload }: SectionProps) {
    return (
        <div>
            <label className={FIELD_LABEL_CLASS}>Details</label>
            {/* Same editor/document the dashboard renders via `EventBody`. `readOnly`
                is explicit - RichEditor isn't native, so `disabled` fieldset misses it. */}
            <div className="border border-gray-300 rounded-md overflow-hidden">
                <RichEditor
                    value={draft.bodyDoc ?? EMPTY_TIPTAP_DOC}
                    onChange={(doc) => set('bodyDoc', doc)}
                    onImageUpload={onImageUpload}
                    allowCallouts={true}
                    allowLayoutBlocks={true}
                    allowTextStyling={true}
                    enableImageCaptions
                    chrome="flush"
                    minHeightPx={BODY_EDITOR_MIN_HEIGHT_PX}
                    readOnly={!canManage}
                />
            </div>
            {!isAnnouncement && (
                <p className="text-[11px] text-gray-400 mt-1">
                    Shown in the sign-up modal and in the confirmation email.
                </p>
            )}
        </div>
    );
}

// The pop-up/email-only fields left once title, subtitle and badge tag moved
// to `BannerFields`. Topic and badge label are read only by the meeting modal
// and the confirmation email, so an announcement carries neither.
function DetailsFields(props: SectionProps) {
    const { draft, set, errors, touched, isAnnouncement } = props;
    return (
        <div>
            <label className={SUBSECTION_LABEL_CLASS}>Details</label>
            <div className="mt-2 flex flex-col gap-4">
                {!isAnnouncement && (
                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <label className={FIELD_LABEL_CLASS}>Topic *</label>
                            <input
                                type="text"
                                value={draft.topic}
                                onChange={(e) => set('topic', e.target.value)}
                                className={inputClass(touched && Boolean(errors.topic))}
                            />
                        </div>
                        <div>
                            <label className={FIELD_LABEL_CLASS}>Badge label *</label>
                            <input
                                type="text"
                                value={draft.badgeLabel}
                                onChange={(e) => set('badgeLabel', e.target.value)}
                                className={inputClass(touched && Boolean(errors.badgeLabel))}
                            />
                        </div>
                    </div>
                )}
                <BodyEditor {...props} />
            </div>
        </div>
    );
}

// The RSVP call-to-action copy - grouped with the rest of what a meeting
// *says* rather than with Appearance's theme/icon/alignment, which is purely
// how the banner/pop-up *look*.
function ActionFields({ draft, set, errors, touched }: SectionProps) {
    return (
        <div>
            <label className={SUBSECTION_LABEL_CLASS}>Call to action</label>
            <div className="mt-2 grid grid-cols-1 gap-4">
                <div>
                    <label className={FIELD_LABEL_CLASS}>Action label *</label>
                    <input
                        type="text"
                        value={draft.actionLabel}
                        onChange={(e) => set('actionLabel', e.target.value)}
                        className={inputClass(touched && Boolean(errors.actionLabel))}
                    />
                    {touched && errors.actionLabel && (
                        <p className="text-red-500 text-xs mt-1">{errors.actionLabel}</p>
                    )}
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={draft.countdownToEvent}
                        onChange={(e) => set('countdownToEvent', e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                    />
                    Show countdown to event
                </label>
            </div>
        </div>
    );
}

/** Banner + Pop-up + Details(topic/badge/body) + call-to-action merged - all
 *  of it is "what this event says", the group an author revisits most after
 *  the first save. */
export function ContentSection(props: SectionProps) {
    const { isAnnouncement } = props;
    return (
        <div className="flex flex-col gap-5">
            <BannerFields {...props} />
            <EventPopupSection {...props} />
            <DetailsFields {...props} />
            {!isAnnouncement && <ActionFields {...props} />}
        </div>
    );
}
